<template>
  <div>
    <ButtonMenu
      v-if="editable && calendarsList?.length > 0 && calendar"
      :offset="[0, 4]"
      placement="bottom-start"
    >
      <template #btn>
        <BaseButton
          v-if="display === 'summary-next-to-tag'"
          kind="auxiliary"
          :text="summary"
        >
          <template #icon>
            <RTag
              class="calendar-tag"
              :theme="`calendar-${calendar.color}`"
              :tooltip="hasTooltip ? calendar.connection : undefined"
              clickable
              :has-padding="hasPadding"
              :compact="compact"
            />
          </template>
        </BaseButton>
        <RTag
          v-else-if="display === 'icon-only' || display === 'summary-in-tag'"
          class="calendar-tag"
          :text="display === 'icon-only' ? '' : summary"
          :theme="`calendar-${calendar.color}`"
          :tooltip="
            hasTooltip
              ? display === 'icon-only'
                ? summary
                : calendar.connection
              : undefined
          "
          clickable
          :has-padding="hasPadding"
          :compact="compact"
        >
          <template v-if="display === 'summary-in-tag'" #right>
            <ChevronDownIcon class="chevron" />
          </template>
        </RTag>
      </template>

      <template #content>
        <div
          v-for="group in groupedCalendars"
          :key="group.connection"
          class="calendar-group"
        >
          <div v-if="groupedCalendars.length > 1" class="connection-header">
            {{ group.connection }}
          </div>
          <div
            v-for="cal in group.calendars"
            :key="cal.id"
            class="calendar-element"
            @click="changeCalendar(cal.id)"
          >
            <RTag
              :theme="`calendar-${cal.color}`"
              :has-padding="hasPadding"
              :compact="compact"
            />
            <span class="calendar-name">{{ cal.summary }}</span>
          </div>
        </div>
      </template>
    </ButtonMenu>
    <BaseButton
      v-else-if="display === 'summary-next-to-tag' && calendar"
      kind="auxiliary"
      :text="summary"
      state="disabled"
    >
      <template #icon>
        <RTag
          :theme="`calendar-${calendar.color}`"
          :tooltip="hasTooltip ? calendar.connection : undefined"
          :has-padding="hasPadding"
          :compact="compact"
        />
      </template>
    </BaseButton>
    <RTag
      v-else-if="
        (display === 'icon-only' || display === 'summary-in-tag') && calendar
      "
      :text="display === 'icon-only' ? '' : summary"
      :theme="`calendar-${calendar.color}`"
      :tooltip="
        hasTooltip
          ? display === 'icon-only'
            ? summary
            : calendar.connection
          : undefined
      "
      :has-padding="hasPadding"
      :compact="compact"
    />
  </div>
</template>

<script lang="ts">
import { filter, groupBy, isEmpty, sortBy } from "lodash-es";
import { computed, defineComponent, PropType, toRefs } from "vue";

import { Calendar } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import RTag from "@/components/elements/RTag.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import * as inject from "@/components/inject";

export default defineComponent({
  components: {
    BaseButton,
    ChevronDownIcon,
    ButtonMenu,
    RTag,
  },
  props: {
    compact: { type: Boolean, default: false },
    editable: { type: Boolean, default: false },
    hasPadding: { type: Boolean, default: true },
    hasTooltip: { type: Boolean, default: true },
    value: { type: String, required: true },
    display: {
      type: String as PropType<
        "icon-only" | "summary-in-tag" | "summary-next-to-tag"
      >,
      default: "summary-next-to-tag",
    },
  },
  emits: {
    "update:value": (_calendarId: string) => true,
  },
  async setup(props, { emit }) {
    const { value } = toRefs(props);
    const calendars = inject.calendars();
    const calendar = computed(() =>
      isEmpty(value.value)
        ? (calendars.value?.default ?? null)
        : (calendars.value?.byId.get(value.value) ?? null)
    );
    const summary = computed<string>(() =>
      calendar.value ? calendar.value.summary : ""
    );
    const calendarsList = computed(() => {
      return sortBy(
        filter(
          calendars.value?.all ?? [],
          (c: Calendar) => c.isWritable && !c.disabled
        ),
        (c) => (c.role === "default" ? 0 : c.role === "primary" ? 1 : 2)
      );
    });

    const groupedCalendars = computed(() => {
      if (!calendarsList.value) return [];
      const grouped = groupBy(calendarsList.value, "connection");
      return Object.entries(grouped).map(([connection, calendars]) => ({
        connection,
        calendars,
      }));
    });

    const changeCalendar = (calendarId: string) => {
      emit("update:value", calendarId);
    };
    return {
      calendar,
      calendarsList,
      groupedCalendars,
      changeCalendar,
      summary,
    };
  },
});
</script>

<style lang="scss" scoped>
.calendar-tag .chevron {
  padding: 8px;
  margin: -9px -5px -9px 0;
}

.calendar-group {
  &:not(:last-child) {
    margin-bottom: 8px;
  }
}

.connection-header {
  @extend %font-small-regular;
  padding: 4px 8px;
  @include themed() {
    color: t($grey-50);
  }
}

.calendar-element {
  display: flex;
  flex-direction: row;
  padding: 6px 8px;
  border-radius: $border-radius-m;
  column-gap: 10px;
  align-items: center;
  min-width: 260px;
  @include transition(background-color 100ms ease-in-out);
  &:hover {
    @include themed() {
      background-color: t($bg-menu-hover);
    }
  }
  span {
    @extend %font-small-regular;
    @include themed() {
      color: t($grey-50);
    }
  }

  .calendar-name {
    @extend %font-caption-regular;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    @include themed() {
      color: t($grey-80);
    }
  }
}
</style>
